import { Model, Factory, Response, belongsTo, trait } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import {
  rand,
  randRecentDate,
  randNumber,
  randSentence,
  randFullName,
} from '@ngneat/falso'

import type { SchemaAttributes } from '../types.js'
import { pageable } from './utils.js'
import type { MirageServer, MockServer } from './server.js'
import { getTodayDateTimeISO } from '@/utils/date/getter.js'

export type NotificationData = SchemaAttributes<'Notification'>

export const model = (Model as ModelDefinition<NotificationData>).extend({
  recipient: belongsTo('user'),
})

export const factory = Factory.extend<NotificationData>({
  role() {
    return rand(['subscriber', 'target'])
  },
  created() {
    return randRecentDate().toISOString()
  },
  read() {
    return rand([null, randRecentDate().toISOString()])
  },
  action_type() {
    return rand(['created', 'updated', 'deleted'])
  },
  sub_type() {
    return ''
  },
  content_type_name() {
    return rand(['appointment', 'consultation', 'deleted'])
  },
  object_id() {
    return randNumber().toString()
  },
  message_content() {
    return randSentence({ length: randNumber({ min: 2, max: 5 }) }).join(' ')
  },
  message_error() {
    return ''
  },
  message_subject() {
    return ''
  },
  legacy_url() {
    return rand(['#', ''])
  },
  afterCreate(notification, server) {
    if (!notification.recipient) {
      const recipient = server.schema.first('user')
      notification.update({ recipient })
    }
  },

  withAppointmentStarting: trait({
    role() {
      return 'subscriber'
    },
    action_type() {
      return 'created'
    },
    sub_type() {
      return 'appointment-starting'
    },
    content_type_name() {
      return 'appointment'
    },
    message_content() {
      return 'Appointment starting soon.'
    },
    legacy_url() {
      return ''
    },
  }),

  withClientArrivedToConsultation: trait({
    role() {
      return 'target'
    },
    action_type() {
      return 'created'
    },
    content_type_name() {
      return 'consultation'
    },
    message_content() {
      return 'Client arrived to consultation.'
    },
    message_subject() {
      return randFullName()
    },
    legacy_url() {
      return '#'
    },
  }),

  withClientWaitingForDischarge: trait({
    role() {
      return 'subscriber'
    },
    action_type() {
      return 'updated'
    },
    sub_type() {
      return 'status-waiting-for-discharge'
    },
    content_type_name() {
      return 'consultation'
    },
    message_content() {
      return 'Client waiting for discharge.'
    },
    message_subject() {
      return randFullName()
    },
    legacy_url() {
      return '#'
    },
  }),

  withClientHasBeenWaitingForTooLong: trait({
    role() {
      return 'subscriber'
    },
    action_type() {
      return 'updated'
    },
    sub_type() {
      return 'status-waited-for-too-long'
    },
    content_type_name() {
      return 'consultation'
    },
    message_content() {
      return 'Client has been waiting for too long.'
    },
    message_subject() {
      return randFullName()
    },
    legacy_url() {
      return '#'
    },
  }),

  withError: trait({
    action_type() {
      return 'updated'
    },
    content_type_name() {
      return 'debtletterprintlog'
    },
    message_content() {
      return 'Failed to activate Invoice Express integration for selected departments.'
    },
    message_error() {
      return 'Example error message'
    },
  }),

  withVetEnvoySuccess: trait({
    role() {
      return 'target'
    },
    action_type() {
      return 'updated'
    },
    sub_type() {
      return 'status-success'
    },
    content_type_name() {
      return 'vet envoy microchip'
    },
    message_content() {
      return 'VetEnvoy: microchip for Milo (345) successfully registered!'
    },
    message_error() {
      return ''
    },
    message_subject() {
      return ''
    },
    legacy_url() {
      return '#'
    },
  }),

  withVetEnvoyError: trait({
    role() {
      return 'target'
    },
    action_type() {
      return 'updated'
    },
    sub_type() {
      return 'status-error'
    },
    content_type_name() {
      return 'vet envoy microchip'
    },
    message_content() {
      return 'VetEnvoy microchip number for Milo (345) failed!'
    },
    message_error() {
      return 'Something went wrong, please contact Support or try again later.'
    },
    message_subject() {
      return ''
    },
    legacy_url() {
      return '#'
    },
  }),

  isRead: trait({
    read() {
      return randRecentDate().toISOString()
    },
  }),

  isUnread: trait({
    read() {
      return null
    },
  }),

  isRecent: trait({
    created() {
      return getTodayDateTimeISO()
    },
  }),
})

export function routes(server: MockServer) {
  server.resource('notification')

  server.get('/notifications', function (this: MirageServer, schema, request) {
    const notifications = schema.all('notification')

    // unread count must consider all notifications, not just those returned by pagination
    const unread = notifications.filter((n) => !n.read).length

    // sort by id descending (newest first) — matches real backend Meta.ordering = ("-id",)
    const sorted = notifications.sort(
      (a, b) => parseInt(b.id ?? '0') - parseInt(a.id ?? '0'),
    )

    // paginate first
    const { data, meta } = pageable(sorted, request)

    // and finally construct the response
    return {
      ...this.serialize(data),
      meta: {
        pagination: meta,
        unread_count: unread,
      },
    }
  })

  server.post('/notifications/mark_read', function (this: MockServer, schema) {
    schema.all('notification').update('read', getTodayDateTimeISO())
    return new Response(204)
  })
}

export function seeds(server: MockServer) {
  server.create(
    'notification',
    'withAppointmentStarting',
    'isUnread',
    'isRecent',
  )
  server.create('notification', 'withClientArrivedToConsultation', 'isUnread')
  server.create('notification', 'withClientHasBeenWaitingForTooLong', 'isRead')
  server.create('notification', 'withClientWaitingForDischarge', 'isRead')
  server.create('notification', 'withError', 'isRead')
}
